import { createAsyncSaga } from '@wahaaabello/redux-async-actions';
import throttle from 'lodash/throttle';
import { Action } from 'redux-actions';
import { SubmissionError } from 'redux-form';
import { eventChannel } from 'redux-saga';
import { all, call, delay, put, race, take, takeLatest } from 'redux-saga/effects';
import * as api from '../../../api';
import { AuthResponse, LogInParams } from '../../../api';
import { requester } from '../../../api/config';
import SessionCountdownMessage from '../../../components/SessionCountdownMessage';
import { LOG_OUT_TIMEOUT } from '../../../constants';
import { Alert, parseQueryString } from '../../../helpers/utils';
import history from '../../../history';
import SessionStorage from '../../../storage';
import uiActions from '../ui/actions';
import authActions from './actions';

const locationEventChannel = eventChannel(emit =>
  history.listen(location => {
    emit(location);
  }),
);

const requestEventChannel = eventChannel(emit => {
  const id = requester.interceptors.request.use(config => {
    emit(config);
    return config;
  });
  return () => {
    requester.interceptors.request.eject(id);
  };
});

const responseEventChannel = eventChannel(emit => {
  const id = requester.interceptors.response.use(
    response => {
      emit(response);
      return response;
    },
    error => {
      emit(error);
      return Promise.reject(error);
    },
  );
  return () => {
    requester.interceptors.response.eject(id);
  };
});

const createBrowserEventChannel = (eventType: string) =>
  eventChannel(emit => {
    const listener = throttle(evt => {
      emit(evt);
    }, 1000);
    window.addEventListener(eventType, listener);
    return () => {
      window.removeEventListener(eventType, listener);
    };
  });

const keyPressEventChannel = createBrowserEventChannel('keypress');
const mouseMoveEventChannel = createBrowserEventChannel('mousemove');
const touchEventChannel = createBrowserEventChannel('touchstart');

function* sessionCountDown() {
  while (true) {
    const { request, idle } = yield race({
      navigate: take(locationEventChannel),
      keyPress: take(keyPressEventChannel),
      mouseMove: take(mouseMoveEventChannel),
      touchStart: take(touchEventChannel),
      request: take(requestEventChannel),
      idle: delay(LOG_OUT_TIMEOUT),
    });
    if (request) {
      yield take(responseEventChannel);
    } else if (idle) {
      const shouldLogOut = new Promise(resolve => {
        const { hide } = Alert.info('', {
          actionBtnText: 'Cancel',
          MessageComponent: SessionCountdownMessage,
          hideAfter: 0,
          callback() {
            resolve(true);
            hide?.();
          },
          onClick() {
            resolve(false);
            hide?.();
          },
        });
      });
      if (yield shouldLogOut) {
        yield put(authActions.logOut({ goHome: false }));
        break;
      }
    }
  }
}

function* saveToken(response: AuthResponse, cb = () => {}) {
  const { message, status, usertoken } = response;
  if (status === 1) {
    yield call(SessionStorage.setItem, 'userToken', usertoken);
    yield put(authActions.logInSuccess(usertoken));
    yield call(cb);
    return response;
  }
  throw new SubmissionError({ _error: new Error(message) });
}

function* logIn({ payload }: Action<LogInParams>) {
  try {
    const response = yield call(api.logIn, payload, {
      alertErrorMessage: true,
    });
    return yield call(saveToken, response);
  } catch (error) {
    yield put(authActions.logInFailure(error));
    throw error;
  }
}

function* register({ payload }: Action<any>) {
  try {
    const response = yield call(api.register, payload, {
      alertErrorMessage: true,
    });

    if (response.status === 1) {
      window.snaptr('track', 'SIGN_UP', {
        sign_up_method: 'register_success',
        user_email: payload.email,
        user_phone_number: payload.phone,
      });
    }

    return yield call(saveToken, response, function* callback() {
      yield put(authActions.registerSuccess(response));
    });
  } catch (error) {
    yield put(authActions.registerFailure(error));
    throw error;
  }
}

function* logOut({ payload: { goHome = true } = {} }) {
  yield call(SessionStorage.setItem, 'userToken', '');

  yield put(uiActions.clearRef());
  if (goHome) {
    let nextLocation = process.env.REACT_APP_PIGGYVEST_LOGOUT;
    const { search } = history.location;
    if (search) {
      const [, params = {}] = parseQueryString(search);
      if (params.next) {
        nextLocation = params.next;
      }
    }
    if (nextLocation) {
      window.location.assign(nextLocation);
    }
  }
}

export default function* authSaga() {
  yield all([
    takeLatest(authActions.logInRequest.toString(), createAsyncSaga(logIn)),
    takeLatest(authActions.registerRequest.toString(), createAsyncSaga(register)),
    takeLatest(authActions.logOut, logOut),
    takeLatest(authActions.sessionCountDown.toString(), sessionCountDown),
  ]);
}
